import { useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import {
  Band,
  BookedWorkCell,
  BookingLedgerRow,
  GroupShell,
  ItemSubtotalStrip,
  LedgerHeader,
  Money,
  PercentCaption,
  TemplateLedgerRow,
  VariationLedgerRow,
  canCancelWork,
  percentOf,
  type LedgerCollapse,
  type LedgerColumn,
} from '../../../shared/components/ledger';
import { formatCurrency } from '../../../shared/utils/currency';
import {
  DEMO_WORK_TYPE,
  SITE,
  itemBooked,
  plotProgress,
  plotTarget,
  plotTotal,
  type LabourItem,
  type Variation,
} from './data';
import { WorkHoverCard } from './WorkHoverCard';

export const SITE_COLUMNS: LedgerColumn[] = [
  { key: BookedWorkCell.Ref, label: 'Ref', flex: 1 },
  { key: BookedWorkCell.BookedBy, label: 'Booked by', flex: 1.2 },
  { key: BookedWorkCell.When, label: 'When', flex: 1.1 },
];

interface RowContext {
  collapse: LedgerCollapse;
  showValues: boolean;
  onOpen: (label: string) => void;
}

function labourItemRows(item: LabourItem, ctx: RowContext): ReactNode {
  const booked = itemBooked(item);
  const count = item.bookings.length;

  if (count === 0) {
    return (
      <TemplateLedgerRow
        key={item.name}
        columns={SITE_COLUMNS}
        collapse={ctx.collapse}
        title={item.name}
        targetAmount={item.target}
        showValues={ctx.showValues}
        onClick={() => ctx.onOpen(item.name)}
        hoverCard={
          <WorkHoverCard
            title={item.name}
            kind="Labour item"
            description="Not booked yet"
            target={item.target}
          />
        }
      />
    );
  }

  const rows = item.bookings.map((booking, index) => (
    <BookingLedgerRow
      key={booking.ref}
      columns={SITE_COLUMNS}
      collapse={ctx.collapse}
      title={item.name}
      status={booking.status}
      reference={booking.ref}
      bookedBy={booking.bookedBy}
      bookedAt={booking.bookedAt}
      amount={booking.amount}
      partLabel={count > 1 ? `${index + 1} of ${count}` : undefined}
      showValues={ctx.showValues}
      onClick={() => ctx.onOpen(booking.ref)}
      menu={{
        label: 'Cancel booking',
        disabled: !canCancelWork(booking.status),
        onSelect: () => ctx.onOpen(`Cancel ${booking.ref}`),
      }}
      hoverCard={
        <WorkHoverCard
          title={item.name}
          kind={
            count > 1
              ? `Labour item · booking ${index + 1} of ${count}`
              : 'Labour item'
          }
          reference={booking.ref}
          status={booking.status}
          who={booking.bookedBy}
          whenIso={booking.bookedAt}
          amount={booking.amount}
          target={item.target}
          approvedBy={booking.approvedBy}
          approvedAt={booking.approvedAt}
        />
      }
    />
  ));

  if (count === 1 && booked >= item.target) {
    return rows;
  }

  return (
    <Box key={item.name}>
      {rows}
      <ItemSubtotalStrip
        collapse={ctx.collapse}
        percentBooked={percentOf(booked, item.target) ?? 0}
        remaining={Math.max(0, item.target - booked)}
        bookedTotal={booked}
        showValues={ctx.showValues}
      />
    </Box>
  );
}

function variationRow(variation: Variation, ctx: RowContext): ReactNode {
  return (
    <VariationLedgerRow
      key={variation.ref}
      columns={SITE_COLUMNS}
      collapse={ctx.collapse}
      workTypeName={DEMO_WORK_TYPE}
      description={variation.description}
      status={variation.status}
      reference={variation.ref}
      bookedBy={variation.bookedBy}
      bookedAt={variation.bookedAt}
      amount={variation.amount}
      showValues={ctx.showValues}
      onClick={() => ctx.onOpen(variation.ref)}
      menu={{
        label: 'Cancel booking',
        disabled: !canCancelWork(variation.status),
        onSelect: () => ctx.onOpen(`Cancel ${variation.ref}`),
      }}
      hoverCard={
        <WorkHoverCard
          title={variation.description}
          kind={DEMO_WORK_TYPE}
          reference={variation.ref}
          status={variation.status}
          who={variation.bookedBy}
          whenIso={variation.bookedAt}
          amount={variation.amount}
          approvedBy={variation.approvedBy}
          approvedAt={variation.approvedAt}
          rejectedBy={variation.rejectedBy}
          rejectionReason={variation.rejectionReason}
        />
      }
    />
  );
}

interface SiteLedgerDemoProps {
  collapse: LedgerCollapse;
  onOpen: (label: string) => void;
}

export function SiteLedgerDemo({ collapse, onOpen }: SiteLedgerDemoProps) {
  const [showValues, setShowValues] = useState(true);
  const ctx: RowContext = { collapse, showValues, onOpen };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: 2,
          py: 0.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showValues}
              onChange={event => setShowValues(event.target.checked)}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              Show values
            </Typography>
          }
          sx={{ mr: 0 }}
        />
      </Box>
      <LedgerHeader
        columns={SITE_COLUMNS}
        collapse={collapse}
        showValues={showValues}
      />
      {SITE.plots.map(plot => {
        const booked = plotTotal(plot);
        const isTemplate = booked === 0;
        return (
          <GroupShell
            key={plot.number}
            header={(open, toggle) => (
              <Band
                collapse={collapse}
                primary={`Plot ${plot.number}`}
                secondary={plot.gang ?? undefined}
                inline={
                  isTemplate ? undefined : (
                    <PercentCaption percent={plotProgress(plot)} />
                  )
                }
                inlineHiddenBelow={480}
                open={open}
                onToggle={toggle}
                showValues={showValues}
                trailing={
                  isTemplate ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatCurrency(plotTarget(plot))} target
                    </Typography>
                  ) : (
                    <Money value={booked} />
                  )
                }
              />
            )}
          >
            {plot.labourItems.map(item => labourItemRows(item, ctx))}
            {plot.variations.map(variation => variationRow(variation, ctx))}
          </GroupShell>
        );
      })}
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <Button size="small" color="inherit" sx={{ color: 'text.secondary' }}>
          Show more plots
        </Button>
      </Box>
    </Box>
  );
}
